import type { CdekAdService } from '@/types/cdek-ad-service';

export const CDEK_AD_SERVICE_READY_EVENT = 'cdek-ad-service:ready';
export type Unsubscribe = () => void;

export const getCdekAdService = (): CdekAdService | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.cdekAdService ?? null;
};

export const hasCdekAdService = (): boolean => {
  return Boolean(getCdekAdService());
};

type CdekAdServiceReadyEvent = CustomEvent<{
  service: CdekAdService;
}>;

export const isCdekAdServiceReadyEvent = (event: unknown): event is CdekAdServiceReadyEvent => {
  if (!(event instanceof CustomEvent)) {
    return false;
  }

  const detail = event.detail as { service?: unknown } | null;

  return Boolean(detail?.service);
};

/**
 * Подписывает на готовность ad-service.
 * Возвращает функцию отписки от события.
 */
export const subscribeCdekAdServiceReady = (
  onReady: (service: CdekAdService) => void,
): Unsubscribe => {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const currentService = getCdekAdService();

  if (currentService) {
    onReady(currentService);
    return () => undefined;
  }

  const onReadyEvent = (event: Event) => {
    if (isCdekAdServiceReadyEvent(event)) {
      onReady(event.detail.service);
    }
  };

  window.addEventListener(CDEK_AD_SERVICE_READY_EVENT, onReadyEvent);

  return () => {
    window.removeEventListener(CDEK_AD_SERVICE_READY_EVENT, onReadyEvent);
  };
};

export const waitForCdekAdService = async (timeoutMs = 3000): Promise<CdekAdService | null> => {
  const currentService = getCdekAdService();

  if (currentService) {
    return currentService;
  }

  if (typeof window === 'undefined') {
    return null;
  }

  return new Promise((resolve) => {
    const timeoutId = window.setTimeout(() => {
      unsubscribe();
      resolve(getCdekAdService());
    }, timeoutMs);

    const unsubscribe = subscribeCdekAdServiceReady((service) => {
      window.clearTimeout(timeoutId);
      unsubscribe();
      resolve(service);
    });
  });
};
